import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { EventMapPoint } from '@radevents/shared';
import { umamiTrack } from '../services/analytics';

/**
 * Karte für die Ort-/Umkreis-Auswahl: Radius-Kreis um ein Zentrum, alle Events als Pins,
 * die im Radius liegenden farbig hervorgehoben.
 *
 * Der Design-Prototyp zeichnet eine schematische Karte mit fixem Kreis und variablem Maßstab;
 * hier steht die echte OSM-Karte (Leaflet) mit demselben Interaktionsmodell — der Kreis hat die
 * echte Größe in Metern, dafür zoomt die Karte bei jeder Radius-Änderung auf ihn.
 *
 * Drei Dinge kann die Karte über die reine Auswahl hinaus:
 * - **Pin antippen** öffnet eine Kurzinfo mit dem Weg zur Detailseite (neuer Tab, damit die
 *   halbfertige Auswahl im Picker nicht verloren geht).
 * - **Bündel antippen** zoomt hinein, solange das die Pins auseinanderzieht — und zeigt sonst
 *   seine Events als Liste.
 * - **Favoriten** tragen ein Herz und bündeln nur untereinander.
 * - **Permanente Events als zweite Ebene** (`permanentPoints`, zuschaltbar in der Terminliste):
 *   hohle Pins mit gestricheltem Rand, eigene Bündel. Sie zählen nicht zur Trefferzahl.
 * - **Ohne Umkreis** (`radiusActive=false`) verschwinden Kreis und Zentrums-Pin, der Ausschnitt
 *   bleibt stehen und alle Pins sind gleich eingefärbt — die Karte lässt sich dann frei erkunden.
 */

/** Grad der Farb-/Größenunterscheidung aus dem Handoff. */
const BERRY = '#922C57';
const GREY = '#B9B3B3';
/**
 * Grau für hohle Pins außerhalb des Umkreises. Das helle `GREY` trägt nur als Fläche; als dünner
 * gestrichelter Rand auf weißem Grund verschwände der Pin auf der Karte.
 */
const GREY_DARK = '#8F8888';

export interface LatLng { lat: number; lng: number }

interface LocationRadiusMapProps {
  center: LatLng;
  radiusKm: number;
  points: EventMapPoint[];
  /**
   * Umkreis aus: kein Kreis, kein Zentrums-Pin, kein Nachziehen des Ausschnitts, alle Pins gleich
   * eingefärbt — die Karte lässt sich dann frei erkunden, statt immer wieder auf den Radius
   * zurückzuspringen.
   */
  radiusActive?: boolean;
  /**
   * Ids der eigenen Favoriten. Diese Events tragen ein Herz statt des Punkts und bündeln nur
   * untereinander — was man sich gemerkt hat, soll auf der Karte auffindbar bleiben.
   */
  favoriteIds?: Set<number>;
  /**
   * Permanente Events als zweite Ebene über den Terminen: hohl gezeichnet und nur untereinander
   * gebündelt, damit auf einen Blick klar bleibt, was einen Termin hat und was nicht. Gebiete
   * bekommen wie in der Liste „Permanent" ihre Fläche.
   */
  permanentPoints?: EventMapPoint[];
  /** Klick/Drag auf der Karte setzt ein neues Zentrum. */
  onPickCenter?: (coord: LatLng) => void;
  /** Beschriftung der Pille unter dem Zentrums-Pin. */
  centerLabel?: string;
  /** Größe der Event-Pins — mobil etwas kleiner (Handoff: 18×24 vs. 20×26). */
  compact?: boolean;
  /**
   * Vom Overlay verdeckter Streifen am oberen Kartenrand in px. Der Radius-Kreis wird dann in dem
   * Rest zentriert, der sichtbar bleibt — auf dem Phone liegen Suchfeld und Umkreis-Karte über der
   * Karte, ohne diesen Wert säße der Kreis halb darunter.
   */
  insetTop?: number;
  /** Zoomstufe beim Aufbau, solange kein Umkreis den Ausschnitt bestimmt (Übersicht). */
  initialZoom?: number;
  /**
   * Karte steht mitten in einer scrollenden Seite (Startseite), nicht in einem eigenen Screen.
   * Dann zieht ein Finger auf dem Handy die Seite und nicht die Karte, sonst bliebe man beim
   * Scrollen in ihr hängen. Zoomen geht weiter mit zwei Fingern und über die Knöpfe.
   */
  inPage?: boolean;
  /**
   * Öffnet ein Event aus dem Popup im selben Tab (Router statt neuem Tab). Nur dort, wo kein
   * halbfertiger Draft verloren gehen kann. Strg-/Cmd-Klick öffnet weiter einen neuen Tab.
   */
  onOpenEvent?: (id: number) => void;
  /**
   * Hervorgehobenes Event (Maus über seiner Card in der Liste). Es wird nie gebündelt und steht
   * groß über allen anderen, damit man sieht, wo es liegt.
   */
  highlightId?: number | null;
  /**
   * Ohne Umkreis: wechselt der Schlüssel (etwa die gewählte Region), legt sich der Ausschnitt einmal
   * um alle Punkte. Leer lassen, wenn die Punkte über die halbe Welt streuen (ungefilterte Liste).
   */
  fitPointsKey?: string;
  /** Ohne Umkreis und ohne `fitPointsKey`: dieser Ausschnitt beim Aufbau statt `initialZoom`. */
  startBounds?: [[number, number], [number, number]];
  className?: string;
  style?: React.CSSProperties;
}

/** Luftlinie in km (Haversine) — dieselbe Rechnung wie die Radius-Suche im Backend. */
export function distanceKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const lat1 = a.lat * Math.PI / 180;
  const lat2 = b.lat * Math.PI / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Wie viele Events liegen im Radius? Speist die Live-Trefferzahl im Picker. */
export function countInRadius(points: EventMapPoint[], center: LatLng, radiusKm: number): number {
  let n = 0;
  for (const p of points) if (distanceKm(center, { lat: p.lat, lng: p.lng }) <= radiusKm) n++;
  return n;
}

/**
 * Tropfen-Pin als SVG.
 *
 * Bewusst ein echter Pfad statt des `clip-path`-Polygons aus dem Prototyp: das Polygon ist auf
 * eine feste Größe hin gezeichnet und wird bei 18 px kantig und unrund. Der Pfad — Kreis oben,
 * sauber auslaufende Spitze — bleibt bei jeder Größe scharf, und das Loch in der Mitte ist ein
 * echter Kreis statt eines aufgesetzten Punkts.
 */
function pinIcon(inside: boolean, compact: boolean, favorite = false, permanent = false): L.DivIcon {
  if (permanent) return permanentPinIcon(inside, compact);
  // Favoriten immer in der großen Größe, auch außerhalb des Umkreises: sie sollen auffindbar
  // bleiben, ohne dass die Farbe ihre Aussage („liegt im Umkreis") verliert.
  const big = inside || favorite;
  const w = big ? (compact ? 19 : 21) : (compact ? 14 : 15);
  const h = Math.round(w * 4 / 3);
  const color = inside ? BERRY : GREY;
  return L.divIcon({
    className: '',
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    // Popup über dem Pin-Kopf statt an der Spitze — dort deckte es genau den Pin zu, den man
    // gerade angetippt hat.
    popupAnchor: [0, -h + 2],
    tooltipAnchor: [0, -h - 2],
    html:
      `<svg width="${w}" height="${h}" viewBox="-1 -1 26 34" style="display:block;` +
      // Der helle Favoriten-Pin braucht einen kräftigeren Schatten, sonst verschwindet er auf
      // hellem Kartengrund (Städte sind fast weiß).
      `filter:drop-shadow(0 1px ${favorite ? 3 : 2}px rgba(0,0,0,${favorite ? 0.5 : 0.35}));opacity:${inside ? 1 : 0.9}">` +
      `<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" ` +
      `fill="${favorite ? '#fff' : color}" stroke="${favorite ? color : '#fff'}" stroke-width="${favorite ? 2.6 : 1.6}"/>` +
      (favorite ? heartMark(color) : `<circle cx="12" cy="12" r="${inside ? 4.4 : 4}" fill="#fff"/>`) +
      '</svg>',
  });
}

/** Pin des Events, über dessen Card die Maus steht: groß, in Marke, mit dunklem Rand. */
function highlightIcon(): L.DivIcon {
  const w = 30;
  const h = 40;
  return L.divIcon({
    className: '',
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h + 2],
    tooltipAnchor: [0, -h - 2],
    html:
      `<svg width="${w}" height="${h}" viewBox="-1 -1 26 34" style="display:block;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45))">` +
      `<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${BERRY}" stroke="#2C2C2C" stroke-width="1.8"/>` +
      '<circle cx="12" cy="12" r="4.6" fill="#fff"/></svg>',
  });
}

/**
 * Pin eines permanenten Events auf der Terminkarte: hohl, gestrichelter Rand, voller Punkt.
 *
 * Hohl, weil das Event keinen Termin hat — der volle Pin bleibt dem Datum vorbehalten. Der
 * gestrichelte Rand trennt ihn vom Favoriten, der ebenfalls hell ist, aber ein Herz trägt. Die
 * Farbe sagt wie überall nur „im Umkreis" oder nicht.
 */
function permanentPinIcon(inside: boolean, compact: boolean): L.DivIcon {
  const w = inside ? (compact ? 19 : 21) : (compact ? 15 : 16);
  const h = Math.round(w * 4 / 3);
  const color = inside ? BERRY : GREY_DARK;
  return L.divIcon({
    className: '',
    iconSize: [w, h],
    iconAnchor: [w / 2, h],
    popupAnchor: [0, -h + 2],
    tooltipAnchor: [0, -h - 2],
    html:
      `<svg width="${w}" height="${h}" viewBox="-1.5 -1.5 27 35" style="display:block;` +
      'filter:drop-shadow(0 1px 3px rgba(0,0,0,0.45))">' +
      '<path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" ' +
      `fill="#fff" stroke="${color}" stroke-width="2.6" stroke-dasharray="4.2 2.6"/>` +
      `<circle cx="12" cy="12" r="4.2" fill="${color}"/>` +
      '</svg>',
  });
}

/**
 * Lesezeichen im Pin-Kopf — die Markierung für „vorgemerkt".
 *
 * Favoriten sind **invertiert**: heller Pin, farbiger Rand, farbiges Herz. Nur das Symbol zu
 * tauschen reichte nicht — auf Pin-Größe war der Unterschied zwischen Punkt und Herz auf der
 * Karte nicht zu erkennen. Die Farbe bleibt dabei der Umkreis-Aussage vorbehalten: außerhalb
 * sind Rand und Herz grau, nicht beere.
 */
function heartMark(color: string): string {
  // Seit PR #197 heißt Merken „Vormerken“ mit Lesezeichen; der Pin trägt dasselbe Zeichen.
  return `<path transform="translate(12 12) scale(0.62) translate(-12 -12)" fill="${color}" ` +
    'd="M17 3a2 2 0 0 1 2 2v15a1 1 0 0 1-1.496.868l-4.512-2.578a2 2 0 0 0-1.984 0l-4.512 2.578A1 1 0 0 1 5 20V5a2 2 0 0 1 2-2z"/>';
}

/** Text, der in HTML landet — Titel und Ort kommen aus der Datenbank. */
function esc(s: string | null | undefined): string {
  return String(s ?? '').replace(/[<>&"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[c] as string));
}

/**
 * Kopfzeile eines Popups: Datum und Ort. Ein permanentes Event hat nur einen Platzhalter-Termin,
 * dort steht „Permanent", und ohne Startort das Gebiet statt der Stadt.
 */
function pointMeta(p: EventMapPoint): string {
  const date = p.permanent ? 'Permanent' : (p.eventDate ? p.eventDate.split('-').reverse().join('.') : '');
  return [date, p.city || p.areaName].filter(Boolean).map(v => esc(v as string)).join(' · ');
}

/**
 * Inhalt des Pin-Popups: Datum, Ort, Titel, Strecken und der Weg zur Detailseite.
 *
 * Der Link geht in einen neuen Tab. Der Picker arbeitet auf einem Draft (Ort, Umkreis, Filter);
 * würde derselbe Tab wegnavigieren, wäre die halbfertige Auswahl verloren — und ein ungenauer
 * Tipp auf dem Handy führte direkt aus der Suche heraus.
 *
 * `?von=karte` sagt der Detailseite, dass dieser Tab aus dem Popup stammt: ihr „Zurück"-Knopf
 * schließt ihn dann, statt auf die Startseite zu springen (siehe `goBackToList` in EventDetail).
 * Ohne den Marker wäre der Fall nicht von einem Deeplink zu unterscheiden — beide haben keinen
 * Verlauf, aber der Deeplink-Tab gehört dem Nutzer und darf nicht zugehen.
 */
export function eventPopupHtml(p: EventMapPoint, favorite: boolean): string {
  const meta = pointMeta(p);
  const distances = (p.distances || []).length > 0 ? `${(p.distances || []).join(' · ')} km` : '';
  return (
    "<div style=\"font-family:'Hanken Grotesk',sans-serif;min-width:170px\">" +
    (meta ? `<div style="font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#9A9494;margin-bottom:3px">${meta}</div>` : '') +
    `<div style="font-weight:700;font-size:14.5px;line-height:1.25;color:#2C2C2C">${esc(p.title)}</div>` +
    (distances ? `<div style="font-size:12.5px;color:#4A4A4A;margin-top:3px">${esc(distances)}</div>` : '') +
    (favorite ? `<div style="display:flex;align-items:center;gap:5px;font-size:12px;color:${BERRY};margin-top:4px"><svg width="11" height="11" viewBox="0 0 24 24"><path fill="${BERRY}" d="M17 3a2 2 0 0 1 2 2v15a1 1 0 0 1-1.496.868l-4.512-2.578a2 2 0 0 0-1.984 0l-4.512 2.578A1 1 0 0 1 5 20V5a2 2 0 0 1 2-2z"/></svg>Vorgemerkt</div>` : '') +
    `<a href="/events/${p.id}?von=karte" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:9px;font-weight:700;font-size:13px;color:${BERRY};text-decoration:none">Zum Event ↗</a>` +
    '</div>'
  );
}

/**
 * Inhalt des Bündel-Popups: alle Events dieser Stelle als Liste, jedes mit dem Weg zur
 * Detailseite.
 *
 * Nötig, weil Hineinzoomen ein Bündel nur auflöst, wenn seine Events überhaupt auseinander liegen.
 * Zwei Veranstaltungen am selben Ort erben dieselbe Stadtkoordinate — dort blieb der Cluster auf
 * jeder Zoomstufe stehen, und beide Events waren über die Karte schlicht nicht erreichbar.
 *
 * Sortiert nach Datum; Links wie im Einzel-Popup in einem neuen Tab und mit `?von=karte`.
 */
export function clusterPopupHtml(points: EventMapPoint[], favorite: boolean, permanent = false): string {
  const rows = points.slice()
    .sort((a, b) => String(a.eventDate ?? '').localeCompare(String(b.eventDate ?? '')))
    .map(p => {
      const meta = pointMeta(p);
      return `<a href="/events/${p.id}?von=karte" target="_blank" rel="noopener noreferrer" ` +
        `data-event-id="${p.id}" style="display:block;padding:7px 0;border-top:1px solid #ECE8E8;text-decoration:none">` +
        (meta ? `<div style="font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#9A9494;margin-bottom:2px">${meta}</div>` : '') +
        `<div style="font-weight:700;font-size:13.5px;line-height:1.25;color:${BERRY}">${esc(p.title)} ↗</div>` +
        '</a>';
    }).join('');
  return (
    "<div style=\"font-family:'Hanken Grotesk',sans-serif;min-width:190px;max-width:230px\">" +
    "<div style=\"font-family:'Space Mono',monospace;font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#9A9494\">" +
    `${points.length} ${favorite ? 'vorgemerkte Events' : permanent ? 'permanente Events' : 'Events'} an dieser Stelle</div>` +
    // Bei vielen Events auf derselben Koordinate darf die Liste die Karte nicht überwachsen.
    `<div style="max-height:216px;overflow-y:auto">${rows}</div>` +
    '</div>'
  );
}

/**
 * Bündel mehrerer Events an fast derselben Stelle.
 *
 * Ohne das kleben in Ballungsräumen zwei Dutzend Pins übereinander zu einem Fleck — man sieht
 * weder einzelne Events noch wie viele es sind. Ein Cluster fasst immer nur Events **desselben**
 * Radius-Zustands zusammen, damit die Farbe eindeutig bleibt: Beere = so viele im Umkreis.
 * Favoriten bündeln ebenfalls nur untereinander und tragen dann ein Herz neben der Zahl.
 */
function clusterIcon(count: number, inside: boolean, favorite = false, permanent = false): L.DivIcon {
  if (permanent) return permanentClusterIcon(count, inside);
  const size = (count < 10 ? 30 : count < 100 ? 36 : 42) + (favorite ? 5 : 0);
  const color = inside ? BERRY : GREY;
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    // Über dem Bündel statt in seiner Mitte: die Liste deckte sonst genau den Kreis zu, den man
    // gerade angetippt hat — dieselbe Korrektur wie beim Einzel-Pin.
    popupAnchor: [0, -size / 2 - 4],
    html:
      // Favoriten-Bündel invertiert wie ihr Pin: heller Grund, farbiger Rand, farbige Schrift.
      `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${favorite ? '#fff' : color};` +
      `border:${favorite ? 3 : 2.5}px solid ${favorite ? color : '#fff'};box-sizing:border-box;` +
      `box-shadow:0 2px 6px rgba(0,0,0,${favorite ? 0.4 : 0.3});` +
      `opacity:${inside ? 1 : 0.9};display:flex;align-items:center;justify-content:center;gap:1px;` +
      `color:${favorite ? color : '#fff'};font-family:'Space Mono',monospace;font-weight:700;` +
      `font-size:${count < 100 ? 12 : 11}px;line-height:1">${count}` +
      (favorite ? `<svg width="9" height="9" viewBox="0 0 24 24" style="display:block"><path fill="${color}" d="M12 17.4s-5.1-3.3-5.1-6.6a2.75 2.75 0 0 1 5.1-1.45 2.75 2.75 0 0 1 5.1 1.45c0 3.3-5.1 6.6-5.1 6.6z"/></svg>` : '') +
      '</div>',
  });
}

/** Bündel permanenter Events: hohl mit gestricheltem Rand wie ihr Pin. */
function permanentClusterIcon(count: number, inside: boolean): L.DivIcon {
  const size = count < 10 ? 32 : count < 100 ? 38 : 44;
  const color = inside ? BERRY : GREY_DARK;
  return L.divIcon({
    className: '',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2 - 4],
    html:
      `<div style="width:${size}px;height:${size}px;border-radius:50%;background:#fff;` +
      `border:2.5px dashed ${color};box-sizing:border-box;box-shadow:0 2px 6px rgba(0,0,0,0.35);` +
      `display:flex;align-items:center;justify-content:center;color:${color};` +
      `font-family:'Space Mono',monospace;font-weight:700;font-size:${count < 100 ? 12 : 11}px;line-height:1">${count}</div>`,
  });
}

/** Zentrums-Pin: Kreis mit weißem Diamanten, darunter Stiel und Ortspille. */
function centerIcon(label: string | undefined, color: string): L.DivIcon {
  const pill = label
    ? `<span style="position:absolute;top:44px;left:50%;transform:translateX(-50%);background:#2C2C2C;color:#fff;` +
      `font-family:'Hanken Grotesk',sans-serif;font-weight:700;font-size:12px;border-radius:999px;padding:4px 10px;` +
      'white-space:nowrap;max-width:180px;overflow:hidden;text-overflow:ellipsis">' +
      label.replace(/[<>&]/g, '') + '</span>'
    : '';
  return L.divIcon({
    className: '',
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    html:
      '<div style="position:relative;width:34px;height:34px">' +
      `<div style="width:34px;height:34px;border-radius:50%;background:${color};border:3px solid #fff;` +
      'box-sizing:border-box;box-shadow:0 6px 16px rgba(0,0,0,0.28);display:flex;align-items:center;justify-content:center">' +
      '<span style="width:9px;height:9px;background:#fff;transform:rotate(45deg);border-radius:2px"></span></div>' +
      '<span style="position:absolute;top:34px;left:50%;transform:translateX(-50%);width:2px;height:9px;background:#fff"></span>' +
      pill +
      '</div>',
  });
}

/**
 * Popup an einen Pin hängen — samt Zählung der tatsächlich geöffneten Detailseiten.
 *
 * Ohne diese Zahl bliebe eine Vermutung, ob die Pins als Einstieg in die Detailseite taugen; der
 * Zähler hängt am Link, nicht am Öffnen des Popups, denn nur der Klick ist das Weitergehen.
 */
/**
 * Kam der letzte Klick von einer Maus? Gefragt wird je Klick, nicht einmal beim Laden: `(hover: hover)`
 * blieb in der Handy-Ansicht der Entwicklerwerkzeuge und auf Geräten mit Maus und Touch auf dem
 * Stand des Seitenaufrufs, und dann öffnete auch ein Fingertipp gleich einen neuen Tab.
 */
let letzterZeiger = 'mouse';
if (typeof document !== 'undefined') {
  document.addEventListener('pointerdown', e => { letzterZeiger = e.pointerType || 'mouse'; }, true);
}
const mausKlick = () => letzterZeiger === 'mouse';

/** Klick auf einen Event-Link im Popup: ohne Zusatztaste über den Router im selben Tab. */
function openInPage(link: Element, id: number, open?: (id: number) => void) {
  // Mit Maus bleibt der Link, wie er ist: neuer Tab, die Karte mit ihrem Ausschnitt bleibt stehen.
  if (!open) return;
  link.addEventListener('click', ev => {
    const me = ev as MouseEvent;
    if (mausKlick() || me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) return;
    ev.preventDefault();
    open(id);
  });
}

/** Vorschau beim Überfahren: Datum und Ort, Titel. Der Weg zur Seite bleibt im Popup per Klick. */
export function eventTooltipHtml(p: EventMapPoint): string {
  const meta = pointMeta(p);
  return (
    (meta ? `<div class="karte-vorschau-meta">${meta}</div>` : '') +
    `<div class="karte-vorschau-titel">${esc(p.title)}</div>`
  );
}

function bindEventPopup(marker: L.Marker, p: EventMapPoint, favorite: boolean, open?: (id: number) => void): L.Marker {
  // Mit Maus (27.10.2026): Überfahren zeigt, welches Event es ist, ein Klick öffnet es gleich in
  // einem neuen Tab; die Karte bleibt im alten Tab stehen. Mit dem Finger gibt es kein Überfahren,
  // dort öffnet der erste Tipp das Popup mit Titel und Link (28.10.2026). Deshalb kein `bindPopup`:
  // das öffnete bei jedem Klick, auch mit Maus.
  marker.bindTooltip(eventTooltipHtml(p), { direction: 'top', className: 'karte-vorschau', opacity: 1 });
  const popup = L.popup({ maxWidth: 250, autoPanPadding: [16, 16] });
  marker.on('click', () => {
    if (mausKlick()) {
      umamiTrack('karte-event-geoeffnet', { id: p.id });
      window.open(`/events/${p.id}?von=karte`, '_blank', 'noopener');
      return;
    }
    marker.closeTooltip();
    const map = (marker as unknown as { _map?: L.Map })._map;
    if (!map) return;
    // Inhalt je Öffnen neu, sonst hängen am Link nach dem zweiten Tipp zwei Zähler.
    popup.setLatLng(marker.getLatLng()).setContent(eventPopupHtml(p, favorite));
    // Über dem Pin statt auf ihm: `popupAnchor` des Symbols plus der Leaflet-Abstand von 7 px.
    popup.options.offset = L.point(marker.options.icon?.options.popupAnchor ?? [0, 0]).add([0, 7]);
    map.openPopup(popup);
    const link = popup.getElement()?.querySelector('a');
    link?.addEventListener('click', () => umamiTrack('karte-event-geoeffnet', { id: p.id }), { once: true });
    if (link) openInPage(link, p.id, open);
  });
  return marker;
}

/**
 * Dasselbe für ein Bündel: jeder Eintrag der Liste zählt als geöffnete Detailseite, damit die
 * Zahl aus `bindEventPopup` weiter das Ganze misst und nicht nur die Einzel-Pins.
 */
function bindClusterPopup(marker: L.Marker, points: EventMapPoint[], favorite: boolean, permanent = false, open?: (id: number) => void): L.Marker {
  marker.bindPopup(clusterPopupHtml(points, favorite, permanent), { maxWidth: 260, autoPanPadding: [16, 16] });
  marker.on('popupopen', e => {
    (e as L.PopupEvent).popup.getElement()?.querySelectorAll('a[data-event-id]').forEach(link => {
      link.addEventListener(
        'click',
        () => umamiTrack('karte-event-geoeffnet', { id: Number(link.getAttribute('data-event-id')) }),
        { once: true }
      );
      openInPage(link, Number(link.getAttribute('data-event-id')), open);
    });
  });
  return marker;
}

/**
 * Kantenlänge der Cluster-Zelle in Bildschirmpixeln. Etwas größer als ein Pin breit ist, damit
 * benachbarte Marker sich nicht mehr überlappen können.
 */
const CLUSTER_CELL_PX = 46;

/**
 * Wartezeit, bevor der Kartenausschnitt einem geänderten Radius folgt. Lang genug, dass ein Zug
 * am Regler nur einen Kachelsatz kostet statt eines pro Schritt; kurz genug, dass es nach dem
 * Loslassen sofort wirkt.
 */
const FIT_DEBOUNCE_MS = 350;

/** Wie weit ein angetipptes Bündel aufzieht — Schritt und Endstufe. */
const CLUSTER_ZOOM_STEP = 3;
const CLUSTER_MAX_ZOOM = 14;

/**
 * Zerfällt das Bündel, wenn die Karte auf `zoom` geht?
 *
 * Dieselbe Rasterung wie in `redrawPins`, und `project` rechnet in absoluten Weltpixeln — die
 * Antwort hängt damit nur am Zoom, nicht am Ausschnitt. Ein „nein" heißt: Hineinzoomen ist hier
 * eine Sackgasse (gleiche Koordinate, oder die Endstufe ist erreicht), der Klick muss stattdessen
 * die Liste zeigen.
 */
function splitsAtZoom(map: L.Map, items: EventMapPoint[], zoom: number): boolean {
  const cellOf = (p: EventMapPoint) => {
    const pt = map.project([p.lat, p.lng], zoom);
    return `${Math.floor(pt.x / CLUSTER_CELL_PX)}:${Math.floor(pt.y / CLUSTER_CELL_PX)}`;
  };
  const first = cellOf(items[0]);
  return items.some(p => cellOf(p) !== first);
}

/**
 * Wie viele Marker gleichzeitig gezeichnet werden. Leaflet-DivIcons sind DOM-Knoten; bei ein paar
 * tausend Events würde die Karte beim Zoomen ruckeln.
 *
 * Bis 26.10.2026 lag die Grenze bei 250 und griff auf der großen Karte der Startseite ständig: dort
 * stehen bei weiter Sicht über 250 Bündel samt Rand. Welche wegfielen, hing an der Reihenfolge, in
 * der die Punkte gesammelt wurden, also tauchten beim leichten Verschieben mitten in der Karte
 * Events auf und verschwanden. Jetzt gehen sichtbare Bündel immer vor den Rand, und die Grenze ist
 * so hoch, dass sie bei unserem Bestand nicht greift.
 */
const MAX_MARKERS = 1200;

export default function LocationRadiusMap({
  center, radiusKm, points, onPickCenter, centerLabel, compact = false, insetTop = 0,
  radiusActive = true, favoriteIds, permanentPoints, initialZoom = 8, inPage = false, onOpenEvent, highlightId = null,
  fitPointsKey, startBounds, className, style,
}: LocationRadiusMapProps) {
  const divRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const centerMarkerRef = useRef<L.Marker | null>(null);
  const pinLayerRef = useRef<L.LayerGroup | null>(null);
  // Aktuelle Props für Handler/Redraws, ohne die Karte neu aufzubauen. Die Favoriten-Ids als
  // leere Menge statt undefined — das erspart jeder Zeichenrunde eine Fallunterscheidung.
  const noFavorites = useRef(new Set<number>()).current;
  const noPoints = useRef<EventMapPoint[]>([]).current;
  const stateRef = useRef({ center, radiusKm, points, compact, radiusActive, favoriteIds: favoriteIds ?? noFavorites, permanentPoints: permanentPoints ?? noPoints, highlightId });
  stateRef.current = { center, radiusKm, points, compact, radiusActive, favoriteIds: favoriteIds ?? noFavorites, permanentPoints: permanentPoints ?? noPoints, highlightId };
  const onPickRef = useRef(onPickCenter);
  onPickRef.current = onPickCenter;
  const onOpenRef = useRef(onOpenEvent);
  onOpenRef.current = onOpenEvent;
  const openEvent = (id: number) => onOpenRef.current?.(id);

  // Karte einmalig aufbauen. Bewusst ohne Deps: Zentrum/Radius/Pins werden unten inkrementell
  // nachgeführt — ein Neuaufbau bei jedem Slider-Schritt würde die Kacheln neu laden lassen.
  useEffect(() => {
    if (!divRef.current || mapRef.current) return;
    const map = L.map(divRef.current, {
      zoomControl: false, attributionControl: true,
      // Nur ganze Zoomstufen: Zwischenstufen verschieben die Kacheln um Bruchteile eines Pixels,
      // und an den Kanten blitzen helle Linien (26.10.2026 so ausprobiert und verworfen).
      ...(inPage ? { dragging: !L.Browser.mobile, scrollWheelZoom: false } : {}),
    });
    // `karte-kacheln`: gedämpft (App.css). Der Standardstil von OpenStreetMap war mit kräftigen
    // Straßen und lila Grenzen lauter als unsere Pins; verglichen am 26.10.2026 mit Stadia
    // (Smooth, Outdoors, Terrain, Toner Lite, Bright) und CARTO, gewählt: dieselben Kacheln, blasser.
    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 18, attribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a>', className: 'karte-kacheln' });
    // Frühwarnung: scheitern die Kacheln, drosselt oder sperrt uns OpenStreetMap womöglich gerade.
    // Ohne dieses Signal bliebe die Karte einfach grau, ohne dass es irgendwo aufschlüge. Nur ein
    // Ereignis je Kartensitzung — `tileerror` feuert sonst pro fehlgeschlagener Kachel.
    let tileErrorSent = false;
    tiles.on('tileerror', () => {
      if (tileErrorSent) return;
      tileErrorSent = true;
      umamiTrack('karte-kachelfehler');
    });
    tiles.addTo(map);
    // Beide Controls nach links unten: rechts unten sitzt der Standort-Button, der sonst
    // die OSM-Attribution verdeckt (die rechtlich sichtbar bleiben muss).
    // Reihenfolge ist wesentlich: in unteren Ecken schiebt Leaflet jedes neue Control *vor* die
    // bestehenden, das zuletzt hinzugefügte liegt also oben. Attribution zuerst → sie sitzt
    // unten am Kartenrand, der Zoom darüber.
    map.attributionControl.setPosition('bottomleft');
    // Ohne Leaflet-Präfix (Flagge, „Leaflet“); Pflicht ist nur der Nachweis „© OpenStreetMap“.
    map.attributionControl.setPrefix(false);
    L.control.zoom({ position: 'bottomleft' }).addTo(map);
    map.setView([center.lat, center.lng], initialZoom);
    circleRef.current = L.circle([center.lat, center.lng], {
      radius: radiusKm * 1000, color: 'rgba(146,44,87,0.55)', weight: 2,
      fillColor: BERRY, fillOpacity: 0.13, interactive: false,
    }).addTo(map);
    pinLayerRef.current = L.layerGroup().addTo(map);
    centerMarkerRef.current = L.marker([center.lat, center.lng], {
      icon: centerIcon(centerLabel, compact ? BERRY : '#2C2C2C'), zIndexOffset: 1000, draggable: true,
    }).addTo(map);
    centerMarkerRef.current.on('dragend', e => {
      const ll = (e.target as L.Marker).getLatLng();
      onPickRef.current?.({ lat: ll.lat, lng: ll.lng });
    });
    map.on('click', e => onPickRef.current?.({ lat: e.latlng.lat, lng: e.latlng.lng }));
    map.on('moveend zoomend', () => redrawPins());
    mapRef.current = map;
    redrawPins();
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const redrawPins = () => {
    const map = mapRef.current;
    const layer = pinLayerRef.current;
    if (!map || !layer) return;
    const { center: c, radiusKm: r, points: pts, compact: cp, radiusActive: ra, favoriteIds: favs, permanentPoints: perms, highlightId: hl } = stateRef.current;
    // Etwas über den sichtbaren Rand hinaus sammeln, sonst poppen Cluster beim Verschieben
    // sprunghaft auf, weil ein Nachbarpunkt gerade eben noch draußen lag.
    const visible = map.getBounds();
    const bounds = visible.pad(0.25);
    const zoom = map.getZoom();

    // Rasterung in Bildschirmpixeln: alles, was in dieselbe Zelle fällt, wird gebündelt.
    // Getrennt nach Radius-Zustand, damit ein Cluster nie Events innerhalb und außerhalb
    // vermischt — sonst wäre seine Farbe eine Behauptung, die nicht stimmt.
    const cells = new Map<string, { items: EventMapPoint[]; inside: boolean; favorite: boolean; permanent: boolean; x: number; y: number }>();
    let highlighted: EventMapPoint | null = null;
    const collect = (p: EventMapPoint, permanent: boolean) => {
      if (!bounds.contains([p.lat, p.lng])) return;
      if (!permanent && hl != null && p.id === hl) { highlighted = p; return; }
      // Ohne Umkreis gibt es kein Drinnen und Draußen: dann sind alle Pins gleichwertig.
      const inside = !ra || distanceKm(c, { lat: p.lat, lng: p.lng }) <= r;
      // Auf der permanenten Ebene zählt die Form, nicht das Herz: ein gemerktes permanentes Event
      // trägt den Merker im Popup, sonst gäbe es einen vierten Pin-Typ.
      const favorite = !permanent && favs.has(p.id);
      const pt = map.project([p.lat, p.lng], zoom);
      // Favoriten und permanente Events bilden ihre eigenen Bündel: gemischt verlöre die Form
      // ihre Aussage, gar nicht gebündelt lägen mehrere derselben Stadt exakt übereinander
      // (Events erben die Stadtkoordinate) — sichtbar wäre dann nur der oberste.
      const key = `${permanent ? 'p' : favorite ? 'f' : 'e'}:${inside ? 1 : 0}:${Math.floor(pt.x / CLUSTER_CELL_PX)}:${Math.floor(pt.y / CLUSTER_CELL_PX)}`;
      const cell = cells.get(key);
      if (cell) { cell.items.push(p); cell.x += pt.x; cell.y += pt.y; }
      else cells.set(key, { items: [p], inside, favorite, permanent, x: pt.x, y: pt.y });
    };
    for (const p of pts) collect(p, false);
    for (const p of perms) collect(p, true);

    // Favoriten zuerst, danach die im Radius: greift die Deckelung, dürfen nie die
    // aussagekräftigen Marker fehlen.
    // Die permanente Ebene steht hinter den Terminen gleichen Zustands: sie ist Zugabe, nicht Auswahl.
    // Sichtbar vor Rand: der Rand ist nur Vorrat gegen Aufpoppen beim Verschieben.
    const shown = (cell: { x: number; y: number; items: EventMapPoint[] }) =>
      visible.contains(map.unproject(new L.Point(cell.x / cell.items.length, cell.y / cell.items.length), zoom));
    const rank = (cell: { inside: boolean; favorite: boolean; permanent: boolean; x: number; y: number; items: EventMapPoint[] }) =>
      Number(shown(cell)) * 8 + Number(cell.favorite) * 4 + Number(cell.inside) * 2 + Number(!cell.permanent);
    const drawn = Array.from(cells.values()).sort((a, b) => rank(b) - rank(a));
    layer.clearLayers();
    // Gebiete (permanente Events ohne Startort) als Fläche unter den Pins. Der Pin im Mittelpunkt
    // bleibt, er trägt Popup und Bündelung; die Fläche zeigt nur, dass es kein Startort ist. Nicht
    // anklickbar, sonst schluckte sie den Klick, der den Umkreis-Mittelpunkt setzt.
    for (const p of [...pts, ...perms]) {
      const b = p.areaBounds;
      if (!b) continue;
      const rect = L.latLngBounds([b.south, b.west], [b.north, b.east]);
      if (!bounds.intersects(rect)) continue;
      const color = !ra || distanceKm(c, { lat: p.lat, lng: p.lng }) <= r ? BERRY : GREY;
      L.rectangle(rect, {
        color, weight: 1.5, opacity: 0.7, dashArray: '5 4',
        fillColor: color, fillOpacity: 0.08, interactive: false,
      }).addTo(layer);
    }
    const hp = highlighted as EventMapPoint | null;
    if (hp) {
      bindEventPopup(
        L.marker([hp.lat, hp.lng], { icon: highlightIcon(), zIndexOffset: 2000 }),
        hp, favs.has(hp.id), onOpenRef.current ? openEvent : undefined
      ).addTo(layer);
    }
    for (const cell of drawn.slice(0, MAX_MARKERS)) {
      const n = cell.items.length;
      const z = (cell.favorite ? 400 : 0) + (cell.inside ? 200 : 0) - (cell.permanent ? 50 : 0);
      if (n === 1) {
        const p = cell.items[0];
        // Popup statt Tooltip: erst damit führt der Pin auch irgendwohin. Beides zusammen legte
        // sich auf dem Handy übereinander — dort öffnet ein Tipp den Tooltip mit.
        bindEventPopup(
          L.marker([p.lat, p.lng], { icon: pinIcon(cell.inside, cp, cell.favorite, cell.permanent), zIndexOffset: z }),
          p, cell.favorite || (cell.permanent && favs.has(p.id)), onOpenRef.current ? openEvent : undefined
        ).addTo(layer);
        continue;
      }
      // Cluster sitzt im Schwerpunkt seiner Punkte, nicht in der Zellenmitte — sonst wandert er
      // sichtbar vom eigentlichen Geschehen weg.
      const at = map.unproject(new L.Point(cell.x / n, cell.y / n), zoom);
      const marker = L.marker(at, { icon: clusterIcon(n, cell.inside, cell.favorite, cell.permanent), zIndexOffset: z + 100 });
      const target = Math.min(zoom + CLUSTER_ZOOM_STEP, CLUSTER_MAX_ZOOM);
      if (splitsAtZoom(map, cell.items, target)) {
        // Antippen zoomt hinein, bis das Bündel aufgeht.
        const titles = cell.items.slice(0, 5).map((p: EventMapPoint) => p.title);
        marker
          .bindTooltip(
            `${n} ${cell.favorite ? 'vorgemerkte Events' : cell.permanent ? 'permanente Events' : 'Events'}${cell.inside && ra ? ' im Umkreis' : ''}<br>${titles.join('<br>')}${n > 5 ? `<br>… und ${n - 5} weitere` : ''}`,
            { direction: 'top', offset: [0, -8] }
          )
          .on('click', ev => {
            L.DomEvent.stopPropagation(ev);
            map.flyTo(at, target);
          });
      } else {
        // Weiteres Zoomen zöge die Pins nicht mehr auseinander — dann ist die Liste der einzige
        // Weg zu diesen Events. Ohne Tooltip: er nannte zwar die Titel, führte aber nirgendwohin,
        // und auf dem Handy öffnet ein Tipp beides übereinander.
        bindClusterPopup(marker, cell.items, cell.favorite, cell.permanent, onOpenRef.current ? openEvent : undefined);
      }
      marker.addTo(layer);
    }
  };

  /**
   * Zentrum + Radius nachführen und den Ausschnitt auf den Kreis legen — so füllt der Radius
   * immer die Karte, genau wie im Prototyp (dort über den Maßstab, hier über den Zoom).
   *
   * Der Ausschnitt wird dabei **entkoppelt**: Kreis, Zentrums-Pin und Pin-Farben folgen dem
   * Regler sofort, `fitBounds` erst, wenn er zur Ruhe kommt. Jeder Zoomwechsel lädt einen neuen
   * Kachelsatz — ein Zug von 10 auf 300 km sind 30 Schritte und damit 30 Kachelsätze von
   * OpenStreetMap, deren Nutzungsrichtlinie genau solche Lastspitzen untersagt. Nebenbei ruckelt
   * es dadurch beim Ziehen nicht mehr.
   *
   * Eine Ortswahl springt weiterhin sofort: dort gibt es keine Schrittfolge, und Verzögerung
   * fühlte sich nur träge an.
   */
  const fitTimerRef = useRef<number | undefined>(undefined);
  /** Zuletzt gültige Ausschnitt-Berechnung — auch von außerhalb dieses Effekts aufrufbar (s. u.). */
  const fitRef = useRef<() => void>(() => {});
  const prevViewRef = useRef({ lat: center.lat, lng: center.lng, insetTop, radiusActive });
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !circleRef.current || !centerMarkerRef.current) return;
    circleRef.current.setLatLng([center.lat, center.lng]).setRadius(radiusKm * 1000);
    centerMarkerRef.current.setLatLng([center.lat, center.lng]);
    // Umkreis aus: Kreis *und* Zentrums-Pin verschwinden ganz. Den Kreis nur zu entfärben ließe
    // offen, ob er noch gilt; und der Pin allein behauptete eine Auswahl, die es nicht mehr gibt —
    // beim Übernehmen fällt der Ort ohne Weite ohnehin weg. Auf der Übersichtskarte stünde er nur
    // im Weg.
    if (radiusActive) {
      circleRef.current.addTo(map);
      centerMarkerRef.current.addTo(map);
    } else {
      map.removeLayer(circleRef.current);
      map.removeLayer(centerMarkerRef.current);
    }
    redrawPins();

    const centerMoved = prevViewRef.current.lat !== center.lat || prevViewRef.current.lng !== center.lng;
    // Der verdeckte Streifen wird erst nach dem ersten Rendern gemessen — springt er, muss der
    // Ausschnitt sofort nachziehen statt verzögert, sonst rutscht der Kreis sichtbar nach unten.
    const insetChanged = prevViewRef.current.insetTop !== insetTop;
    const switchedOn = radiusActive && !prevViewRef.current.radiusActive;
    prevViewRef.current = { lat: center.lat, lng: center.lng, insetTop, radiusActive };
    const fit = () => {
      const m = mapRef.current;
      // Oben mehr Rand: das Overlay verdeckt den Streifen, der Kreis landet dadurch in der Mitte
      // des sichtbaren Rests statt in der Mitte des Kartencontainers.
      if (m && circleRef.current && radiusActive) {
        m.fitBounds(circleRef.current.getBounds(), {
          paddingTopLeft: [24, 24 + insetTop],
          paddingBottomRight: [24, 24],
          animate: false,
        });
      }
    };
    fitRef.current = fit;
    window.clearTimeout(fitTimerRef.current);
    // Ohne Umkreis bleibt der Ausschnitt, wie er ist — genau darum geht es beim Erkunden: die
    // Karte darf nicht mehr auf einen Kreis zurückspringen, den es gerade nicht gibt.
    if (!radiusActive) return;
    if (centerMoved || insetChanged || switchedOn) fit();
    else fitTimerRef.current = window.setTimeout(fit, FIT_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center.lat, center.lng, radiusKm, insetTop, radiusActive]);

  const fittedKeyRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || radiusActive || !fitPointsKey || fittedKeyRef.current === fitPointsKey || points.length === 0) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng] as [number, number]));
    // Erst nach dem Nachmessen der Größe (siehe unten), sonst passt Leaflet auf die alte Höhe ein.
    const t = window.setTimeout(() => {
      fittedKeyRef.current = fitPointsKey;
      mapRef.current?.invalidateSize();
      mapRef.current?.fitBounds(bounds, { padding: [40, 40], maxZoom: 10, animate: false });
    }, 120);
    return () => window.clearTimeout(t);
  }, [fitPointsKey, points, radiusActive]);

  // Beim Schließen keinen Nachzieher mehr feuern lassen.
  useEffect(() => () => window.clearTimeout(fitTimerRef.current), []);

  useEffect(() => {
    centerMarkerRef.current?.setIcon(centerIcon(centerLabel, compact ? BERRY : '#2C2C2C'));
  }, [centerLabel, compact]);

  // Auch die Favoriten: sie kommen nachgeladen und ändern sich, während die Karte offen ist.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { redrawPins(); }, [points, favoriteIds, permanentPoints, highlightId]);

  /** Startausschnitt (`startBounds`), etwa beim Aufbau und wenn der Ort wieder entfernt wird. */
  const zeigeStart = (animate: boolean) => {
    const m = mapRef.current;
    if (!m || !startBounds) return;
    // `fitBounds` rundet ab und zeigt dann halb Europa; gerundet wird hier zur nächsten ganzen
    // Stufe, die Ränder des Ausschnitts dürfen dafür etwas abgeschnitten sein.
    const b = L.latLngBounds(startBounds);
    const exact = m.getBoundsZoom(b, false, L.point(20, 20));
    const snap = m.options.zoomSnap;
    m.options.zoomSnap = 0;
    const fine = m.getBoundsZoom(b, false, L.point(20, 20));
    m.options.zoomSnap = snap;
    m.setView(b.getCenter(), Math.max(exact, Math.round(fine)), { animate });
  };

  // Ort entfernt (Umkreis aus, keine Region): zurück auf den Startausschnitt statt am alten Ort zu bleiben.
  const warUmkreis = useRef(radiusActive);
  useEffect(() => {
    if (warUmkreis.current && !radiusActive && !fitPointsKey) zeigeStart(true);
    warUmkreis.current = radiusActive;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radiusActive]);

  // Ändert sich die Größe des Containers ohne Fensterwechsel (Teiler zwischen Liste und Karte, eine
  // Seite ausgeblendet), weiß Leaflet nichts davon und zeichnet graue Flächen. Ausschnitt bleibt.
  useEffect(() => {
    const el = divRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let rahmen = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rahmen);
      rahmen = requestAnimationFrame(() => mapRef.current?.invalidateSize({ animate: false }));
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(rahmen); ro.disconnect(); };
  }, []);

  // Der Container ist beim ersten Render oft noch 0 px hoch (Modal öffnet gerade) — Leaflet merkt
  // sich dann die falsche Größe und zeichnet nur einen Kachel-Streifen.
  //
  // Danach muss der Ausschnitt neu gesetzt werden: `invalidateSize` behält das Zentrum bei und
  // rückt den Kreis damit wieder in die *geometrische* Mitte des Containers — der Versatz für den
  // verdeckten Streifen wäre sonst genau dann weg, wenn er gebraucht wird.
  useEffect(() => {
    const t = setTimeout(() => {
      mapRef.current?.invalidateSize();
      fitRef.current();
      if (!radiusActive && !fitPointsKey) zeigeStart(false);
    }, 60);
    return () => clearTimeout(t);
  // Nur beim Aufbau: Startausschnitt und Größe gelten einmal, danach bestimmt der Nutzer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={divRef} className={className} style={{ width: '100%', height: '100%', background: '#E9EBE5', ...style }} />;
}
